import React, { useState, useEffect } from 'react';
import { matchPath, useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ImageRoundedIcon from '@material-ui/icons/ImageRounded';
import SettingsRoundedIcon from '@material-ui/icons/SettingsRounded';
import PresentToAllIcon from '@material-ui/icons/PresentToAll';

import { useVMMutation, useVMState } from '../../../../containers/main';
import BasicDialog from '../../../common/Dialog/BasicDialog';
import entities from '../../../../constants/entities';
import Loader from '../../../common/Loader';
import { compressImage } from '../../../../../../utils';

import Card from './Card';
import CropDialog from './CropDialog';
import ExportChat from './ExportChat';
import FeatureItem from './FeatureItem';
import ImageUpload from './ImageUpload';
import LinkCard from './LinkCard';
import { uploadPlaceholderToS3 } from '../../../../services/VirtualEventSession';
import {
    getQuestionSetByExternalObject,
    saveQuestionSet,
} from '../../../../services/QuestionsAndAnswersService';
import { FontIcon } from 'react-md';
import copyUtils from '../../../../utils/copyUtils';
import { getLocalAppStateAsync } from '../../../../../../../../services/api/db';
import {
    getPollSetByExternalObject,
    savePollSet,
} from '../../../../../Votes/services/PollingService';
import { useTheme } from '../../../../../../../../components/Theme/ThemeContext';

const Separator = styled.div`
    background-color: #efefef;
    height: 8px;
`;

const Section = styled.div`
    align-items: center;
    background: #fff;
    cursor: pointer;
    display: flex;
    transition: all 0.2s linear;
    border-radius: 8px;
    height: 72px;
    width: calc(100% - 56px);
    padding: 12px;
    border: solid 1px #d3d3d3;
    margin: 0 0 16px 56px;
    position: relative;
    overflow: hidden;

    &:hover {
        background-color: rgba(0, 0, 0, 0.04);
    }
`;

const IconWrapper = styled(FontIcon)`
    margin: 0 16px 20px 0;
    color: rgba(0, 0, 0, 0.87) !important;

    ${props =>
        props.right &&
        css`
            position: absolute;
            top: 15px;
            right: 18px;
            margin: 0;
        `}
`;

const SectionLabels = styled.div`
    align-items: center;
    display: flex;
    flex-flow: column;
`;

const SectionTitle = styled.div`
    align-self: stretch;
    flex-grow: 1;
    font-family: Cabin, sans-serif;
    font-size: 15px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.33;
    letter-spacing: normal;
    text-align: left;
    color: rgba(0, 0, 0, 0.87);
`;

const SectionSubTitle = styled.div`
    align-self: stretch;
    flex-grow: 1;
    font-family: Roboto, sans-serif;
    font-size: 13px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.54;
    letter-spacing: normal;
    text-align: left;
    color: rgba(0, 0, 0, 0.6);
    position: relative;
    top: 8px;
    max-width: 230px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const CopyButton = styled.div`
    position: absolute;
    top: 15px;
    right: 18px;
    font-size: 15px;
    color: rgba(0, 0, 0, 0.6);
    font-family: Roboto, sans-serif;

    ${props =>
        props.copied &&
        css`
            align-items: center;
            color: rgba(0, 0, 0, 0.85);
            display: flex;

            i {
                margin: 0 8px 0 0;
            }
        `}
`;

// Strings
const DIALOG_TITLE = 'Session settings';
const PLACEHOLDER_IMAGE_TITLE = 'Placeholder slides image';
const FEATURES_TITLE = 'Features';
const ADVANCED_TITLE = 'Advanced';
const CHAT_LABEL = 'Chat';
const HIDE_VOTES_NUMBER_LABEL = 'Hide number of votes';
const QA_LABEL = 'Q&A';
const ATTENDEES_LABEL = 'Attendees';
const SPEAKER_FRAME_LABEL = 'Speaker Frame';
const SLIDES_FRAME_LABEL = 'Slides Frame';
const SLIDES_IMAGE_UPLOAD_MESSAGE = `Recommended size: 1280x720, PNG or JPG, below 2MB. Larger images can be cropped after uploading.`;
const PRESENTATION_LINK_TITLE = 'Presentation screen link';
const PRESENTATION_TITLE = 'Presentation';

const SettingsModal = ({ onClose, questionSetsId, nonVirtualQA, externalObject }) => {
    const [featureValues, setValues] = useState({
        chat: true,
        qa: true,
        attendees: true,
    });
    const [QAValues, setQAValues] = useState({
        qa: false,
    });
    const [pollSet, setPollSet] = useState(null);
    const [hideVotesNumber, setHideVotesNumber] = useState(false);
    const [questionSet, setQuestionSet] = useState({});
    const [eventName, setEventName] = useState(null);
    const [eventUniqueName, setEventUniqueName] = useState('');
    const [loading, setLoading] = useState(false);
    const [activeRadioBoxIndex, setActiveRadioBoxIndex] = useState(0);
    // Image state
    const [previewPlaceholder, setPreviewPlaceholder] = useState(null);
    const [previewSpeakerPlaceholder, setPreviewSpeakerPlaceholder] = useState(null);
    const [file, setFile] = useState(null);
    const [uploadImageModal, setUploadImageModal] = useState(null);
    const [uploadSpeakerImageModal, setUploadSpeakerImageModal] = useState(null);
    const [temporaryImage, setTemporaryImage] = useState(null);
    const [temporarySpeakersImage, setTemporarySpeakersImage] = useState(null);
    const [copied, setCopied] = useState(false);

    const stateCtx = useVMState();
    const mutationCtx = useVMMutation();

    const { sessionId, virtualEventSession } = stateCtx;

    const port = process.env.NODE_ENV === 'development' ? `:${window.location.port}` : '';
    const beamerViewLink = `${window.location.protocol}//${window.location.hostname}${port}/event/${
        eventUniqueName || eventName
    }/virtual-beamer-view/${externalObject.data.reference}`;
    const placeHolderUrl = temporaryImage || virtualEventSession.data.presentationPlaceholderUrl;
    const speakersPlaceHolderUrl =
        temporarySpeakersImage || virtualEventSession.data.presentationSpeakerPlaceholderUrl;

    const history = useHistory();

    useEffect(() => {
        let match = matchPath(history.location.pathname, {
            path: '/event/:eventName',
        });

        if (!match) {
            match = matchPath(history.location.pathname, {
                path: '/kiosk/:eventName',
            });
        }

        if (match && match.params.eventName) {
            setEventName(match.params.eventName);
        }
    }, [history]);

    useEffect(() => {
        (async () => {
            const { events } = await getLocalAppStateAsync();
            if (events) {
                const currentEvent = events.find(e => e.id === externalObject.data.Event.reference);

                setEventUniqueName(currentEvent.uniqueName);
            }
        })();
    }, [externalObject]);

    useEffect(() => {
        const loadQA = async () => {
            const qSet = await getQuestionSetByExternalObject(externalObject.data.id);

            if (questionSet) {
                setQuestionSet(qSet);
                setQAValues({
                    qa: qSet.state && qSet.state === 'open',
                });
            }
        };

        const fetchPollSet = async () => {
            const pollSet = await getPollSetByExternalObject(externalObject.data.id);
            setHideVotesNumber(pollSet.hideVotesNumber);
            setPollSet(pollSet);
        };

        if (nonVirtualQA) {
            loadQA();
            fetchPollSet();
            return;
        }

        const {
            showChat,
            showQA,
            showAttendees,
            showBlockedAttendees,
            hideVotesNumber,
        } = virtualEventSession.data;

        setValues({
            chat: showChat,
            qa: showQA,
            attendees: showAttendees,
        });
        setHideVotesNumber(hideVotesNumber);
        setActiveRadioBoxIndex(showBlockedAttendees ? 1 : 0);
    }, []);

    const onFeatureChange = event => {
        const { name, checked } = event.target;
        setValues({
            ...featureValues,
            [name]: checked,
        });
    };

    const onToggleHideVotesNumbers = event => {
        const { checked } = event.target;
        setHideVotesNumber(checked);
    };

    const onQASettingChange = event => {
        const { name, checked } = event.target;
        setQAValues({
            [name]: checked,
        });
    };

    const onImageChange = async file => {
        const resizedFile = await compressImage(file);

        setFile(resizedFile);
        setPreviewPlaceholder(URL.createObjectURL(resizedFile));
        setUploadImageModal(true);
    };

    const onSpeakerImageChange = async file => {
        const resizedFile = await compressImage(file);

        setFile(resizedFile);
        setPreviewSpeakerPlaceholder(URL.createObjectURL(resizedFile));
        setUploadSpeakerImageModal(true);
    };

    const onCopyClick = () => {
        copyUtils.copyTextToClipboard(beamerViewLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const convertBase64ToFile = speakerPlaceholder => {
        var arr = speakerPlaceholder
                ? temporarySpeakersImage.split(',')
                : temporaryImage.split(','),
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        return new File([u8arr], file.name, { type: file.type });
    };

    const uploadPlaceholderImage = async (speakerPlaceholder = false) => {
        const imageFile = convertBase64ToFile(speakerPlaceholder);

        mutationCtx.setVirtualEventSession({
            ...virtualEventSession,
            uploadRequestStatus: entities.requestStatus.loading,
        });

        const response = await uploadPlaceholderToS3(imageFile, sessionId);

        mutationCtx.setVirtualEventSession({
            ...virtualEventSession,
            uploadRequestStatus: entities.requestStatus.success,
            data: {
                ...virtualEventSession.data,
                [speakerPlaceholder
                    ? 'presentationSpeakerPlaceholderUrl'
                    : 'presentationPlaceholderUrl']: response.link,
            },
        });

        stateCtx.socket.emit('updateData', {
            objectId: sessionId,
            virtualEventSession: {
                mode: virtualEventSession.data.mode,
                status: virtualEventSession.data.status,
                [speakerPlaceholder
                    ? 'presentationSpeakerPlaceholderUrl'
                    : 'presentationPlaceholderUrl']: response.link,
            },
        });

        if (speakerPlaceholder) {
            setPreviewSpeakerPlaceholder(null);
            setUploadSpeakerImageModal(false);
        } else {
            setPreviewPlaceholder(null);
            setUploadImageModal(false);
        }
    };

    const uploadTemporaryPlaceholderImage = image => {
        setTemporaryImage(image);
        setUploadImageModal(false);
    };

    const uploadTemporarySpeakerPlaceholderImage = image => {
        setTemporarySpeakersImage(image);
        setUploadSpeakerImageModal(false);
    };

    const onSave = async () => {
        setLoading(true);

        if (nonVirtualQA) {
            await saveQuestionSet({
                ...questionSet,
                state: QAValues.qa ? 'open' : 'closed',
            });
            const data = {};
            data.objectId = externalObject.data.id;
            await savePollSet({ ...pollSet, hideVotesNumber });
            stateCtx.socket.emit('setQuestions', data);
            stateCtx.socket.emit('updatePollSet', {
                objectId: sessionId,
                id: questionSetsId,
            });
        } else {
            const updatedValues = {
                showBlockedAttendees: activeRadioBoxIndex === 1,
                showChat: featureValues.chat,
                showQA: featureValues.qa,
                showAttendees: featureValues.attendees,
                hideVotesNumber,
            };

            if (previewPlaceholder) {
                await uploadPlaceholderImage();
            }

            if (previewSpeakerPlaceholder) {
                await uploadPlaceholderImage(true);
            }

            stateCtx.socket.emit('updateData', {
                objectId: sessionId,
                virtualEventSession: {
                    status: virtualEventSession.data.status,
                    ...updatedValues,
                },
            });

            const questionSetState = featureValues.qa ? 'open' : 'closed';

            stateCtx.socket.emit('updatePollSet', {
                objectId: sessionId,
                id: questionSetsId,
                state: questionSetState,
            });
        }

        setLoading(false);
        onClose();
    };

    const makePresentationUrl = () =>
        `${window.location.protocol}//${window.location.hostname}${port}/qa-presentation/${externalObject.data.id}`;

    if (uploadImageModal) {
        return (
            <CropDialog
                previewPlaceholder={previewPlaceholder}
                onClose={() => setUploadImageModal(false)}
                onSave={uploadTemporaryPlaceholderImage}
            />
        );
    }

    if (uploadSpeakerImageModal) {
        return (
            <CropDialog
                previewPlaceholder={previewSpeakerPlaceholder}
                onClose={() => setUploadSpeakerImageModal(false)}
                onSave={uploadTemporarySpeakerPlaceholderImage}
            />
        );
    }

    const dialogButtons = [
        { title: 'Save', variant: 'contained', color: 'primary', onClick: onSave },
        { title: 'Cancel', variant: 'contained', color: 'inherit', onClick: onClose },
    ];

    return (
        <BasicDialog
            visible
            title={DIALOG_TITLE}
            onClose={onClose}
            contentStyle={{ padding: 0 }}
            options={dialogButtons}
        >
            {!nonVirtualQA && (
                <>
                    <Card title={PLACEHOLDER_IMAGE_TITLE} icon={<ImageRoundedIcon />}>
                        <ImageUpload
                            subtitle={SLIDES_IMAGE_UPLOAD_MESSAGE}
                            placeHolderUrl={placeHolderUrl}
                            onImageChange={onImageChange}
                            imageTypeText={SLIDES_FRAME_LABEL}
                            onDeleteImage={() => {
                                stateCtx.socket.emit('updateData', {
                                    objectId: sessionId,
                                    virtualEventSession: {
                                        presentationPlaceholderUrl: null,
                                    },
                                });
                            }}
                        />
                        <ImageUpload
                            placeHolderUrl={speakersPlaceHolderUrl}
                            onImageChange={onSpeakerImageChange}
                            imageTypeText={SPEAKER_FRAME_LABEL}
                            onDeleteImage={() => {
                                stateCtx.socket.emit('updateData', {
                                    objectId: sessionId,
                                    virtualEventSession: {
                                        presentationSpeakerPlaceholderUrl: null,
                                    },
                                });
                            }}
                        />
                    </Card>
                    <Separator />
                    <Card title={FEATURES_TITLE} icon={<CheckCircleIcon />}>
                        <FeatureItem
                            name="chat"
                            onChange={onFeatureChange}
                            label={CHAT_LABEL}
                            checked={featureValues.chat}
                        />
                        <FeatureItem
                            name="qa"
                            onChange={onFeatureChange}
                            label={QA_LABEL}
                            checked={featureValues.qa}
                        />
                        <FeatureItem
                            name="attendees"
                            onChange={onFeatureChange}
                            label={ATTENDEES_LABEL}
                            checked={featureValues.attendees}
                        />
                    </Card>
                </>
            )}
            {nonVirtualQA && (
                <>
                    <Card title={FEATURES_TITLE} icon={<CheckCircleIcon />}>
                        <FeatureItem
                            name="qa"
                            onChange={onQASettingChange}
                            label={QA_LABEL}
                            checked={QAValues.qa}
                        />
                    </Card>
                </>
            )}
            <Separator />
            <Card title={PRESENTATION_TITLE} icon={<PresentToAllIcon />}>
                {!nonVirtualQA && (
                    <Section onClick={onCopyClick}>
                        <SectionLabels>
                            <SectionTitle>{PRESENTATION_LINK_TITLE}</SectionTitle>
                            <SectionSubTitle>{beamerViewLink}</SectionSubTitle>
                        </SectionLabels>
                        <CopyButton copied={copied}>
                            {copied && (
                                <React.Fragment>
                                    <IconWrapper>check</IconWrapper>
                                    Copied
                                </React.Fragment>
                            )}
                            {!copied && 'Copy'}
                        </CopyButton>
                    </Section>
                )}
                <FeatureItem
                    name="hideVotesNumber"
                    onChange={onToggleHideVotesNumbers}
                    label={HIDE_VOTES_NUMBER_LABEL}
                    checked={hideVotesNumber}
                    noBorderTop
                />
            </Card>
            <Separator />
            <Card title={ADVANCED_TITLE} icon={<SettingsRoundedIcon />}>
                <ExportChat sessionId={sessionId} />
            </Card>
            {nonVirtualQA && (
                <>
                    {/*<LinkCard title={'Beamer link'} url={''} />*/}
                    <LinkCard title={PRESENTATION_LINK_TITLE} url={makePresentationUrl()} />
                </>
            )}
            {loading && <Loader />}
        </BasicDialog>
    );
};

export default SettingsModal;
